import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuRoute,
  LuSparkles,
  LuArrowRight,
  LuRefreshCw,
  LuCircle,
  LuCircleCheck,
  LuLoader,
  LuUpload,
  LuFileText,
  LuMic,
  LuBriefcase,
  LuGraduationCap,
  LuAward,
  LuWrench,
  LuBookOpen,
  LuInfo,
  LuCalendar,
  LuClock,
  LuTarget,
  LuExternalLink,
  LuPartyPopper,
  LuBuilding2,
  LuSend,
} from "react-icons/lu";
import { Btn, Card, Badge, Note, Skel, Field, MatchRing, Sheet, Select } from "../components/Ui";
import CareerStudyPack from "./CareerStudyPack";
import "./careerJourney.css";

/* Career Journey — AI-first. The intake is a conversation (career_journey_v2
   action "chat"): the coach asks adaptive questions, spots gaps from prior
   answers, then generates the roadmap. Journey Home then shows readiness,
   phase-grouped steps, and an application tracker. */
const JOURNEY_API = "https://us-east1-foursssolutions.cloudfunctions.net/career_journey_v2";

const APP_STATUS_OPTS = [
  { value: "saved", label: "Saved" },
  { value: "applied", label: "Applied" },
  { value: "interviewing", label: "Interviewing" },
  { value: "offer", label: "Offer" },
  { value: "rejected", label: "Rejected" },
];

const PHASE_META = [
  { key: "foundations", label: "Foundations", sub: "Core skills to build first" },
  { key: "build", label: "Build proof", sub: "Projects & credentials that show ability" },
  { key: "hireready", label: "Get hire-ready", sub: "Resume & positioning for the role" },
  { key: "apply", label: "Apply & interview", sub: "Find roles and practice" },
  { key: "land", label: "Land the role", sub: "Secure and evaluate an offer" },
];

const TYPE_META = {
  interview: { icon: LuMic, label: "Interview" },
  resume: { icon: LuFileText, label: "Resume" },
  apply: { icon: LuBriefcase, label: "Apply" },
  skill: { icon: LuGraduationCap, label: "Skill" },
  project: { icon: LuWrench, label: "Project" },
  cert: { icon: LuAward, label: "Certification" },
  course: { icon: LuBookOpen, label: "Course" },
};

const STATUS_NEXT = { todo: "in_progress", in_progress: "done", done: "todo" };
const STATUS_OPTS = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" },
];

function typeMeta(t) {
  return TYPE_META[t] || { icon: LuGraduationCap, label: "Step" };
}
function parseDetail(step) {
  const d = step?.detail;
  if (!d) return {};
  if (typeof d === "object") return d;
  try { return JSON.parse(d); } catch { return {}; }
}
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function CareerJourney() {
  const navigate = useNavigate();
  const userEmail = useSelector((s) => s.auth?.auth?.email) || "";
  const { resumeJson, fileResume } = useSelector((s) => s.profile);
  const hasResume = Boolean(fileResume || resumeJson);

  const [phase, setPhase] = useState("loading"); // loading | empty | roadmap
  const [journey, setJourney] = useState(null);
  const [steps, setSteps] = useState([]);

  // conversational intake
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [needsResume, setNeedsResume] = useState(false);
  const [error, setError] = useState("");
  const chatStartedRef = useRef(false);
  const logRef = useRef(null);

  // roadmap
  const [savingId, setSavingId] = useState("");
  const [apps, setApps] = useState([]);
  const [appCompany, setAppCompany] = useState("");
  const [appTitle, setAppTitle] = useState("");
  const [addingApp, setAddingApp] = useState(false);

  // step detail drawer
  const [activeStep, setActiveStep] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const callChat = useCallback(async (msgs) => {
    const res = await fetch(JOURNEY_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "chat", email_id: userEmail, messages: msgs }),
    });
    return res.json();
  }, [userEmail]);

  const startChat = useCallback(async () => {
    if (!userEmail) return;
    setChatBusy(true); setError(""); setNeedsResume(false);
    try {
      const data = await callChat([]);
      if (data.status === "needs_resume") { setNeedsResume(true); return; }
      setChatMsgs(data.message ? [{ role: "assistant", content: data.message }] : []);
    } catch (err) {
      console.error("Start chat failed:", err);
      setError("We couldn't start the conversation. Please try again.");
    } finally {
      setChatBusy(false);
    }
  }, [userEmail, callChat]);

  const loadJourney = useCallback(async () => {
    if (!userEmail) return;
    setPhase("loading"); setError("");
    try {
      const res = await fetch(JOURNEY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get", email_id: userEmail }),
      });
      const data = await res.json();
      if (data.status === "success" && data.journey) {
        setJourney(data.journey);
        setSteps(Array.isArray(data.steps) ? data.steps : []);
        setPhase("roadmap");
      } else {
        setPhase("empty");
      }
    } catch (err) {
      console.error("Load journey failed:", err);
      setError("We couldn't load your journey. Please try again.");
      setPhase("empty");
    }
  }, [userEmail]);

  useEffect(() => { loadJourney(); }, [loadJourney]);

  // kick off the intake conversation once, when we land on the empty state
  useEffect(() => {
    if (phase === "empty" && hasResume && !chatStartedRef.current) {
      chatStartedRef.current = true;
      startChat();
    }
    if (phase !== "empty") chatStartedRef.current = false;
  }, [phase, hasResume, startChat]);

  // auto-scroll the chat log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [chatMsgs, chatBusy]);

  // load applications on the roadmap
  useEffect(() => {
    if (phase !== "roadmap" || !userEmail) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(JOURNEY_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "list_applications", email_id: userEmail }),
        });
        const data = await res.json();
        if (alive) setApps(Array.isArray(data.applications) ? data.applications : []);
      } catch (err) {
        console.error("Load applications failed:", err);
      }
    })();
    return () => { alive = false; };
  }, [phase, userEmail]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const newMsgs = [...chatMsgs, { role: "user", content: text }];
    setChatMsgs(newMsgs); setChatInput(""); setChatBusy(true); setError("");
    try {
      const data = await callChat(newMsgs);
      if (data.status === "needs_resume") { setNeedsResume(true); return; }
      if (data.message) setChatMsgs((m) => [...m, { role: "assistant", content: data.message }]);
      if (data.done && data.journey) {
        setJourney(data.journey);
        setSteps(Array.isArray(data.steps) ? data.steps : []);
        setTimeout(() => setPhase("roadmap"), 900);
      }
    } catch (err) {
      console.error("Chat failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setChatBusy(false);
    }
  };

  const setStepStatus = async (step, next) => {
    const prev = steps;
    setSteps((cur) => cur.map((s) => (s.step_id === step.step_id ? { ...s, status: next } : s)));
    if (activeStep?.step_id === step.step_id) setActiveStep((a) => ({ ...a, status: next }));
    setSavingId(step.step_id);
    try {
      const res = await fetch(JOURNEY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_step", step_id: step.step_id, journey_id: journey?.journey_id, status: next }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setJourney((j) => j ? {
          ...j,
          ...(typeof data.progress_pct === "number" ? { progress_pct: data.progress_pct } : {}),
          ...(typeof data.readiness_pct === "number" ? { readiness_pct: data.readiness_pct } : {}),
        } : j);
      } else { throw new Error(data.message || "update failed"); }
    } catch (err) {
      console.error("Update step failed:", err);
      setSteps(prev);
      toast.error("Couldn't save that. Please try again.");
    } finally {
      setSavingId("");
    }
  };

  const saveNote = async () => {
    if (!activeStep) return;
    setSavingNote(true);
    try {
      const res = await fetch(JOURNEY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_step", step_id: activeStep.step_id, journey_id: journey?.journey_id, notes: noteDraft }),
      });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "save failed");
      setSteps((cur) => cur.map((s) => (s.step_id === activeStep.step_id ? { ...s, notes: noteDraft } : s)));
      toast.success("Notes saved");
      setActiveStep(null); // close the drawer after saving (Pavithra #4)
    } catch (err) {
      console.error("Save note failed:", err);
      toast.error("Couldn't save your notes. Please try again.");
    } finally {
      setSavingNote(false);
    }
  };

  const addApp = async () => {
    if (!appCompany.trim() && !appTitle.trim()) return;
    setAddingApp(true);
    try {
      const res = await fetch(JOURNEY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "add_application", email_id: userEmail, journey_id: journey?.journey_id,
          company: appCompany.trim(), job_title: appTitle.trim(), status: "saved",
        }),
      });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "add failed");
      setApps((cur) => [{ app_id: data.app_id, company: appCompany.trim(), job_title: appTitle.trim(), status: "saved" }, ...cur]);
      setAppCompany(""); setAppTitle("");
    } catch (err) {
      console.error("Add application failed:", err);
      toast.error("Couldn't add that application.");
    } finally {
      setAddingApp(false);
    }
  };

  const updateApp = async (app, status) => {
    const prev = apps;
    setApps((cur) => cur.map((a) => (a.app_id === app.app_id ? { ...a, status } : a)));
    try {
      const res = await fetch(JOURNEY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_application", app_id: app.app_id, status }),
      });
      const data = await res.json();
      if (data.status !== "success") throw new Error();
    } catch (err) {
      console.error("Update application failed:", err);
      setApps(prev);
      toast.error("Couldn't update status.");
    }
  };

  const openStep = (step) => { setActiveStep(step); setNoteDraft(step.notes || ""); };

  /* A passing self-check completes the step server-side, so mirror that here
     rather than refetching the whole journey. */
  const onPackCompletedStep = useCallback((totals) => {
    if (!activeStep) return;
    setSteps((cur) => cur.map((s) => (s.step_id === activeStep.step_id ? { ...s, status: "done" } : s)));
    setActiveStep((a) => (a ? { ...a, status: "done" } : a));
    setJourney((j) => (j ? {
      ...j,
      ...(typeof totals?.progress_pct === "number" ? { progress_pct: totals.progress_pct } : {}),
      ...(typeof totals?.readiness_pct === "number" ? { readiness_pct: totals.readiness_pct } : {}),
    } : j));
  }, [activeStep]);

  const startNew = () => {
    setChatMsgs([]);
    setNeedsResume(false);
    setError("");
    setPhase("empty"); // effect restarts the conversation
  };

  const nextStep = steps.find((s) => s.status !== "done" && s.status !== "skipped");
  const readiness = journey?.readiness_pct || 0;
  const doneCount = steps.filter((s) => s.status === "done").length;
  const allDone = steps.length > 0 && !nextStep;

  const renderStepCard = (step) => {
    const meta = typeMeta(step.step_type);
    const Icon = meta.icon;
    const done = step.status === "done";
    const saving = savingId === step.step_id;
    const hasNote = step.notes && step.notes.trim();
    return (
      <Card key={step.step_id} className={`cj-step${done ? " done" : ""}`} hover role="button" tabIndex={0}
        onClick={() => openStep(step)} onKeyDown={(e) => (e.key === "Enter" ? openStep(step) : null)}>
        <button type="button" className="cj-check"
          onClick={(e) => { e.stopPropagation(); setStepStatus(step, STATUS_NEXT[step.status] || "done"); }}
          aria-label={done ? "Mark not done" : "Mark done"}
          title={done ? "Done" : step.status === "in_progress" ? "In progress" : "Mark done"}>
          {saving ? <LuLoader size={20} className="cj-spin" /> : done ? <LuCircleCheck size={20} /> : <LuCircle size={20} />}
        </button>
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="cj-step-head">
            <span className="cj-step-order">{step.step_order}</span>
            <span className="cj-step-title">{step.title}</span>
            <Badge tone={done ? "accent" : "plain"}><Icon size={12} /> {meta.label}</Badge>
            {step.status === "in_progress" ? <Badge tone="amber">In progress</Badge> : null}
            {hasNote ? <span className="cj-note-dot" title="Has notes">✎</span> : null}
          </div>
          {step.description ? <p className="t-sm cj-step-desc">{step.description}</p> : null}
        </div>
        <span className="cj-step-chev"><LuArrowRight size={16} /></span>
      </Card>
    );
  };

  /* ---------- loading ---------- */
  if (phase === "loading") {
    return (
      <div className="cj-wrap stack-4">
        <Header />
        <Card className="stack-3"><Skel w="40%" h={20} /><Skel h={70} r={14} /><Skel h={70} r={14} /></Card>
      </div>
    );
  }

  /* ---------- intake conversation ---------- */
  if (phase === "empty") {
    return (
      <div className="cj-wrap stack-4">
        <Header />
        {!hasResume ? (
          <Card className="cj-gate stack-3">
            <span className="cj-gate-ic"><LuUpload size={22} /></span>
            <div className="stack-2">
              <h2 className="t-h3">Add your resume to begin</h2>
              <p className="t-sm">Your journey is built from your skills and experience. Upload your resume and your AI coach will map a path to your goal.</p>
            </div>
            <div><Btn to="/resume-upload" className="btn-mint btn-pill"><LuFileText size={15} /> Upload resume</Btn></div>
          </Card>
        ) : (
          <div className="cj-grid">
            <Card className="cj-chat-card">
              <div className="cj-chat-head">
                <span className="cj-chat-ic"><LuSparkles size={15} /></span>
                <span className="t-label">Your AI career coach</span>
              </div>
              <div className="cj-chat-log" ref={logRef}>
                {chatMsgs.map((m, i) => (
                  <div key={i} className={`cj-msg ${m.role}`}>{m.content}</div>
                ))}
                {chatBusy ? (
                  <div className="cj-msg assistant cj-typing"><span /><span /><span /></div>
                ) : null}
                {needsResume ? (
                  <Note tone="danger"><LuInfo size={16} /><span>
                    We couldn't find your parsed skills yet.{" "}
                    <button type="button" className="cj-link" onClick={() => navigate("/resume-upload")}>Upload your resume</button>{" "}and come back.
                  </span></Note>
                ) : null}
                {error ? (
                  <Note tone="danger"><LuInfo size={16} /><span>{error} <button type="button" className="cj-link" onClick={startChat}>Retry</button></span></Note>
                ) : null}
              </div>
              <div className="cj-chat-input">
                <input
                  className="input"
                  value={chatInput}
                  placeholder="Type your answer…"
                  disabled={chatBusy || needsResume}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendChat(); } }}
                />
                <Btn className="btn-mint btn-pill cj-send" onClick={sendChat} disabled={chatBusy || !chatInput.trim()} aria-label="Send">
                  <LuSend size={16} />
                </Btn>
              </div>
            </Card>

            <Card className="cj-aside stack-3">
              <div className="t-label">What you'll get</div>
              <ul className="cj-gets">
                <li>A target role matched to your goal</li>
                <li>6–10 concrete steps, in order</li>
                <li>Why each step matters + resources</li>
                <li>Notes you can keep on each step</li>
                <li>Readiness you track over time</li>
              </ul>
              <Note tone="info"><LuInfo size={16} /><span>Just answer naturally — your coach adapts to what you say.</span></Note>
            </Card>
          </div>
        )}
      </div>
    );
  }

  /* ---------- roadmap ---------- */
  return (
    <div className="cj-wrap stack-4">
      <Header />

      <Card className="cj-head-card">
        <div className="row" style={{ gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <MatchRing score={readiness} size={68} label="ready" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t-label">Target role</div>
            <div className="t-h3" style={{ marginTop: 2 }}>{journey?.target_role || "Your career goal"}</div>
            {journey?.summary ? <p className="t-sm mt-1">{journey.summary}</p> : null}
            <p className="t-xs mt-1">
              {doneCount} of {steps.length} steps done
              {journey?.timeline_months ? ` · ${journey.timeline_months}-month plan` : ""}
            </p>
          </div>
          <Btn variant="ghost" className="btn-pill" onClick={startNew}><LuRefreshCw size={15} /> New journey</Btn>
        </div>
      </Card>

      {allDone ? (
        <Card className="cj-complete">
          <span className="cj-complete-ic"><LuPartyPopper size={26} /></span>
          <div className="stack-2 grow">
            <div className="t-h3">You've completed your journey! 🎉</div>
            <p className="t-sm">Every step toward <strong>{journey?.target_role}</strong> is done. Ready to aim higher?</p>
          </div>
          <Btn className="btn-mint btn-pill" onClick={startNew}><LuSparkles size={15} /> Start a new journey</Btn>
        </Card>
      ) : nextStep ? (
        <Card className="cj-next" role="button" tabIndex={0} onClick={() => openStep(nextStep)}
          onKeyDown={(e) => (e.key === "Enter" ? openStep(nextStep) : null)}>
          <div className="stack-2 grow" style={{ minWidth: 0 }}>
            <span className="t-label">Your next step</span>
            <div className="t-h3">{nextStep.title}</div>
            {nextStep.description ? <p className="t-sm">{nextStep.description}</p> : null}
          </div>
          {nextStep.linked_feature ? (
            <Btn className="btn-mint btn-pill" onClick={(e) => { e.stopPropagation(); navigate(nextStep.linked_feature); }}>
              Do this <LuArrowRight size={15} />
            </Btn>
          ) : null}
        </Card>
      ) : null}

      <div className="cj-phases">
        {PHASE_META.map((ph) => {
          const phaseSteps = steps
            .filter((s) => (s.phase || "").toLowerCase() === ph.key)
            .sort((a, b) => (a.step_order || 0) - (b.step_order || 0));
          if (!phaseSteps.length) return null;
          const phaseDone = phaseSteps.filter((s) => s.status === "done").length;
          return (
            <section className="cj-phase" key={ph.key}>
              <div className="cj-phase-head">
                <div>
                  <div className="cj-phase-label">{ph.label}</div>
                  <div className="t-xs">{ph.sub}</div>
                </div>
                <span className="cj-phase-count">{phaseDone}/{phaseSteps.length}</span>
              </div>
              <div className="cj-steps">{phaseSteps.map(renderStepCard)}</div>
            </section>
          );
        })}
        {(() => {
          const known = new Set(PHASE_META.map((p) => p.key));
          const orphan = steps.filter((s) => !known.has((s.phase || "").toLowerCase()));
          if (!orphan.length) return null;
          return <div className="cj-steps">{orphan.map(renderStepCard)}</div>;
        })()}
      </div>

      <section className="cj-apps">
        <div className="cj-phase-head">
          <div>
            <div className="cj-phase-label">Applications</div>
            <div className="t-xs">Track the roles you're pursuing</div>
          </div>
          <span className="cj-phase-count">{apps.length}</span>
        </div>
        <Card className="stack-3">
          <div className="cj-appadd">
            <input className="input" placeholder="Company" value={appCompany} onChange={(e) => setAppCompany(e.target.value)} />
            <input className="input" placeholder="Role / title" value={appTitle} onChange={(e) => setAppTitle(e.target.value)} />
            <Btn className="btn-pill" variant="soft" onClick={addApp} loading={addingApp}
              disabled={addingApp || (!appCompany.trim() && !appTitle.trim())}>Add</Btn>
          </div>
          {apps.length ? (
            <div className="cj-applist">
              {apps.map((a) => (
                <div key={a.app_id} className="cj-approw">
                  <span className="cj-app-ic"><LuBuilding2 size={16} /></span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="cj-app-title">{a.job_title || a.company || "Application"}</div>
                    {a.company && a.job_title ? <div className="t-xs">{a.company}</div> : null}
                  </div>
                  <div className="cj-app-status">
                    <Select value={a.status} onChange={(e) => updateApp(a, e.target.value)} options={APP_STATUS_OPTS} ariaLabel="Application status" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="t-sm">No applications yet. As you apply — from Jobs or anywhere — add them here to track saved → applied → interviewing → offer.</p>
          )}
        </Card>
      </section>

      <Sheet open={Boolean(activeStep)} onClose={() => setActiveStep(null)}
        title={activeStep ? `Step ${activeStep.step_order}` : ""} width="min(520px, 100vw)"
        footer={activeStep?.linked_feature ? (
          <Btn full className="btn-pill btn-mint" onClick={() => navigate(activeStep.linked_feature)}>
            Do this now <LuArrowRight size={15} />
          </Btn>
        ) : null}>
        {activeStep ? (
          <StepDetail step={activeStep} noteDraft={noteDraft} setNoteDraft={setNoteDraft}
            onSaveNote={saveNote} savingNote={savingNote} onStatus={(st) => setStepStatus(activeStep, st)}
            email={userEmail} onPackCompletedStep={onPackCompletedStep} />
        ) : null}
      </Sheet>
    </div>
  );
}

function StepDetail({ step, noteDraft, setNoteDraft, onSaveNote, savingNote, onStatus, email, onPackCompletedStep }) {
  const meta = typeMeta(step.step_type);
  const Icon = meta.icon;
  const detail = parseDetail(step);
  const resources = Array.isArray(detail.resources) ? detail.resources : [];
  return (
    <div className="stack-3">
      <div className="stack-2">
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <Badge tone="plain"><Icon size={12} /> {meta.label}</Badge>
          {detail.est_effort ? <Badge tone="plain"><LuClock size={12} /> {detail.est_effort}</Badge> : null}
          {step.target_date ? <Badge tone="plain"><LuCalendar size={12} /> by {fmtDate(step.target_date)}</Badge> : null}
        </div>
        <h3 className="t-h3">{step.title}</h3>
        {step.description ? <p className="t-sm">{step.description}</p> : null}
      </div>

      {detail.why_it_matters ? (
        <div className="cj-detail-block">
          <div className="t-label"><LuTarget size={13} /> Why it matters</div>
          <p className="t-sm">{detail.why_it_matters}</p>
        </div>
      ) : null}

      {resources.length ? (
        <div className="cj-detail-block">
          <div className="t-label"><LuBookOpen size={13} /> Resources</div>
          <ul className="cj-res">
            {resources.map((r, i) => (
              <li key={i}>
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noreferrer" className="t-accent">{r.label || r.url} <LuExternalLink size={12} /></a>
                ) : (r.label || String(r))}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {step.verify_signal ? (
        <div className="cj-detail-block">
          <div className="t-label"><LuCircleCheck size={13} /> You'll know you're done when</div>
          <p className="t-sm">{step.verify_signal}</p>
        </div>
      ) : null}

      {/* keyed by step so switching steps remounts with clean state */}
      {email ? (
        <CareerStudyPack key={step.step_id} email={email} step={step}
          onStepCompleted={onPackCompletedStep} />
      ) : null}

      <div className="cj-detail-block">
        <div className="t-label">Status</div>
        <div className="cj-chiprow">
          {STATUS_OPTS.map((o) => (
            <button key={o.value} type="button" className={`cj-chip sm${step.status === o.value ? " on" : ""}`}
              onClick={() => onStatus(o.value)} aria-pressed={step.status === o.value}>{o.label}</button>
          ))}
        </div>
      </div>

      <Field label="Your notes" hint="Track your progress, links, or anything to remember for this step.">
        <textarea className="input cj-notes" rows={5} value={noteDraft} placeholder="Add a note…" onChange={(e) => setNoteDraft(e.target.value)} />
      </Field>
      <div>
        <Btn className="btn-pill" variant="soft" onClick={onSaveNote} loading={savingNote} disabled={savingNote}>Save notes</Btn>
      </div>
    </div>
  );
}

/* Page title + subtitle live in the top bar now (no duplicate h1 on the page). */
function Header() {
  return null;
}
